import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Feature } from 'ol';
import { default as FillPattern } from 'ol-ext/style/FillPattern';
import { GeoJSON } from 'ol/format';
import { GeoJSONPoint } from 'ol/format/GeoJSON';
import { Geometry, LineString, Point } from 'ol/geom';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Fill, Icon, RegularShape, Stroke, Style } from 'ol/style';
import CircleStyle from 'ol/style/Circle';
import { Observable } from 'rxjs';
import { bufferTime, distinctUntilChanged, map, mergeMap, switchMap } from 'rxjs/operators';

import { CameraCommand, CAMERA_COMMAND_NAME, CAMERA_IMAGE_MODE, CAMERA_RECORDING, UAVCommand, UAV_COMMAND_NAME } from '../../model/uavcommand';
import { GeoFence, UAVStatus, Waypoint } from '../../model/UAVStatus';
import { UrlConfig } from '../../model/urlconfig';
import { UAVCommandQuery } from '../state/uavcommand.query';
import { getCommandIdFromUAVStatus, getUAVIdFromUAVCommandId, UAVsQuery } from '../state/uavs.query';
import { UAVCommandService, DEFAULT_FLIGHT_ALTITUDE, DEFAULT_CAMERA_ALTITUDE } from './uavcommand.service';
import { UAVsService } from './uavs.service';


export const UAVID = 'uavId';
export const UAVCMD_ID = 'uavCMDId';
export const UAVCMD_FEATURE_TYPE = 'featuretype';
export const UAV_GEOFENCE_INCLUSION = 'geofenceinclusion';
export const UAV_GEOFENCE_LAYER_NAME = 'uavgeofence';
export const UAV_WAYPOINT_LAYER_NAME = 'uavwaypoint';
const IS_NEXT_WAYPOINT = 'nextWaypoint';

export enum FEATURE_TYPES {
  POI,
  CAMERA_POINT
}

const styleCache = {
  style: undefined
};

interface CameraCommandOptions {
  point?: GeoJSONPoint;
  mode?: CAMERA_IMAGE_MODE;
  commandName?: CAMERA_COMMAND_NAME;
  zoom?: number;
  recording?: CAMERA_RECORDING;
  snapshot?: boolean;
}


@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class UAVMapService implements UrlConfig{
  droneLayer: VectorLayer<VectorSource>;
  geoFenceLayer: VectorLayer<VectorSource>;
  waypointLayer: VectorLayer<VectorSource>;
  uavGeoFenceSource = new VectorSource();
  uavWaypointSource = new VectorSource();

  constructor(
    private uavService: UAVsService,
    private uavQuery: UAVsQuery,
    private uavCommandQuery: UAVCommandQuery,
    private uavCommandService: UAVCommandService
  ) { }

  updateEntitiesUrl(newUrl: string): void {
    this.uavService.updateEntitiesUrl(newUrl);
    this.uavCommandService.updateEntitiesUrl(newUrl);
  }

  private markedUavId = '';
  uavs: Observable<UAVStatus[]>;
  uavCommands: Observable<UAVCommand[]>;
  private uavCommandSource = new VectorSource();

  public readonly cameraPointStyle = new Style({
    image: new Icon({
      src: 'assets/camera_drone.svg',
    })
  });

  public readonly poiPointStyle = new Style({
    image: new Icon({
      src: 'assets/mapMarker.svg',
    })
  });

  public readonly homePointStyle = new Style({
    image: new Icon({
      src: 'assets/homePointMarker.svg',
    })
  });

  private readonly waypointStyle = new Style({
    image: new CircleStyle({
      radius: 10,
      fill: new Fill({
        color: [58, 12, 163, 1]
      })
    })
  });

  private readonly nextWaypointStyle = new Style({
    image: new CircleStyle({
      radius: 10,
      fill: new Fill({
        color: [181, 23, 158, 1]
      })
    })
  });

  waypointLayerStyle = (feature: Feature) => {
    if (feature.get(UAVID) !== this.markedUavId) { return null; }

    const geometry = feature.getGeometry();

    if (geometry instanceof LineString) {
      const lineStyles = [
        new Style({
          stroke: new Stroke({
            color: [58, 12, 163, 1],
            width: 4,
            lineDash: [1, 8]
          }),
        }),
      ];

      geometry.forEachSegment((start, end) => {
        const dx = end[0] - start[0];
        const dy = end[1] - start[1];
        const rotation = Math.atan2(dy, dx);
        // Arrows
        lineStyles.push(
          new Style({
            geometry: new Point(end),
            image: new Icon({
              src: 'assets/arrow.png',
              color: [255, 255, 255, 1],
              anchor: [0.35, 0.5],
              rotateWithView: true,
              rotation: -rotation,
            }),
          })
        );
      });

      return lineStyles;
    }

    if (geometry instanceof Point) {
      if (feature.get(IS_NEXT_WAYPOINT)) { return this.nextWaypointStyle; }
      else { return this.waypointStyle; }
    }
  };

  geoFenceLayerStyle = (feature: Feature) => {
    if (feature.get(UAVID) !== this.markedUavId) { return null; }

    if (feature.get(UAV_GEOFENCE_INCLUSION)) {
      return new Style({
        stroke: new Stroke({
          color: [58, 12, 163, .75],
          width: 3,
          lineDash: [16, 8]
        })
      });
    } else {
      // Exclusion
      return new Style({
        stroke: new Stroke({
          color: [58, 12, 163, .75],
          width: 3,
          lineDash: [16, 8]
        }),
        fill: new FillPattern({
          color: [58, 12, 163, .65],
          pattern: 'hatch',
          size: 2,
          spacing: 12,
          angle: 45
        })
      });
    }
  };

  // default dataProjection aka the feature format is EPSG:4326
  private readonly geoJSON = new GeoJSON({ featureProjection: 'EPSG:3857' });

  private uavStyle() {
    const style = styleCache.style;
    if (!styleCache.style) {
      const uavStyle = [new Style({
        image: new Icon({
          src: 'assets/UAV.svg',
        })
      }), new Style({
        image: new RegularShape({
          stroke: new Stroke({ color: [0, 0, 0, 0] }),
          points: 4,
          fill: new Fill({ color: [0, 0, 0, 0] }),
          radius: 40, // <--------- control its size
          angle: Math.PI / 4 // angle is in rad
        })
      })];

      styleCache.style = uavStyle;
      return uavStyle;

    }
    return style;
  }

  loadAll() {
    this.uavCommandService.getAll();
    this.uavService.getAll();
    this.uavs = this.uavQuery.selectAllUAVsWithCommand();
    this.uavCommands = this.uavCommandQuery.selectAll();
  }

  getUAVStatusForId(id: string | number) {
    return this.uavQuery.selectEntity(id);
  }

  getUavStatusEntityForId(id: string | number) {
    return this.uavQuery.getEntity(id);
  }

  getUAVCmdForId(id: string | number) {
    return this.uavCommandQuery.selectEntity(id);
  }

  getUAVStatusWithCommandForId(id: string | number) {
    return this.uavQuery.selectWithUAVCommand(id);
  }

  getFirstUAV() {
    return this.uavQuery.selectFirst();
  }

  getDroneStyle() {
    return this.uavStyle;
  }

  getallUAVS() {
    return this.uavQuery.selectAll();
  }

  uavCommandSelectStyle(feature: Feature) {
    const type = feature.get(UAVCMD_FEATURE_TYPE) as FEATURE_TYPES;
    switch (type) {
      case FEATURE_TYPES.POI: return this.poiPointStyle;
      case FEATURE_TYPES.CAMERA_POINT: return this.cameraPointStyle;
      default: return new Style();
    }
  }

  getAllUAVIdsWhenCountChanges() {
    return this.uavQuery.selectCount().pipe(
      distinctUntilChanged(),
      switchMap(() => this.uavs),
      map(uav => uav.map(stat => stat.id)));
  }

  flyToPOI(poi: GeoJSONPoint, newHeight?: number) {
    this.setUAVCommand(UAV_COMMAND_NAME.FLY_TO_POI, poi, newHeight);
  }
  flyToPoiWithCameraCommand(poi: GeoJSONPoint, newHeight?: number, cameraCommandName?: CAMERA_COMMAND_NAME, cameraPos?: GeoJSONPoint) {
    this.setUAVCommand(UAV_COMMAND_NAME.FLY_TO_POI, poi, newHeight, cameraCommandName, cameraPos);
  }

  returnHome() {
    this.setUAVCommand(UAV_COMMAND_NAME.RETURN);
  }

  landUAV() {
    this.setUAVCommand(UAV_COMMAND_NAME.LAND);
  }

  private setUAVCommand(name: UAV_COMMAND_NAME, poi?: GeoJSONPoint, newHeight?: number, cameraComandName?: CAMERA_COMMAND_NAME, cameraPos?: GeoJSONPoint) {
    if (!this.markedUavId) {
      return;
    }

    const cmdId = getCommandIdFromUAVStatus(this.markedUavId);
    const cmd = this.uavCommandQuery.getEntity(cmdId);
    const drone = this.getUavStatusEntityForId(this.markedUavId);

    switch (name) {
      case UAV_COMMAND_NAME.FLY_TO_POI:
        if (poi == null) { console.log('POI must be defined to fly to it.'); return; }
        break;
      case UAV_COMMAND_NAME.LAND:
        // Land at current location. Maybe force altitude to zero?
        poi = { ...drone.location };
        break;
      case UAV_COMMAND_NAME.RETURN:
        // homePosition's coordinates are not extensible, we have to pass a deep copy.
        // TODO: Might be a good idea to remove destination marker from the map as it will always overlap home point.
        poi = { ...drone.homePosition, coordinates: [...drone.homePosition.coordinates] };
        break;
      default:
        console.log('Unknown UAV command.');
    }

    if (cmd) {
      // if we got a new Height using the altitude popover
      if (newHeight) {
        poi.coordinates[2] = newHeight;
      }
      else {
        poi.coordinates[2] = drone?.location?.coordinates[2] ?? DEFAULT_FLIGHT_ALTITUDE;
      }

      const newCommand: UAVCommand = {
        ...cmd,
        command: {
          uavPosition: poi,
          commandName: name
        }
      };

      const cameraCommand: CameraCommand = { ...cmd.cameraCommand };
      if (cameraCommand) {
        cameraCommand.cameraCommandName = cameraComandName ?? cmd.cameraCommand?.cameraCommandName;
        cameraCommand.cameraImageMode = cmd?.cameraCommand?.cameraImageMode;
        cameraCommand.cameraPosition = cameraPos ?? cmd.cameraCommand?.cameraPosition;
      }


      this.uavCommandService.updateUAVCommand(newCommand, poi, drone, name, cameraCommand);
    } else {
      this.uavCommandService.createNewCommand(cmdId, poi, drone, name, null);
    }
  }

  setCameraComand({ point, mode, commandName, zoom, recording, snapshot }: CameraCommandOptions) {
    if (!this.markedUavId) { return; }

    const uavCmdId = getCommandIdFromUAVStatus(this.markedUavId);
    const cmd = this.uavCommandQuery.getEntity(uavCmdId);
    const drone = this.uavQuery.getEntity(this.markedUavId);

    if (cmd) {
      this.uavCommandService.updateCameraCommand(
        cmd,
        point ?? cmd.cameraCommand?.cameraPosition,
        mode ?? cmd.cameraCommand?.cameraImageMode,
        commandName ?? cmd.cameraCommand?.cameraCommandName,
        zoom ?? cmd.cameraCommand.zoomLevel,
        recording ?? cmd.cameraRecording,
        snapshot ?? cmd.doSnapshot ?? false
      );
    } else {
      // TODO: this actually sets UAVCommand's POI to what is supposed to be camera POI
      // Default camera command should probably be STOW
      this.uavCommandService.createNewCommand(uavCmdId, point, drone);
    }
  }

  clearCmdFeatureStyleWhenDroneChanged(droneId: string) {
    this.uavCommandSource.forEachFeature(feature => feature.get(UAVID) === droneId ? this.uavCommandSelectStyle(feature) : null);
  }

  getGeoFenceLayer() {
    this.geoFenceLayer = new VectorLayer({
      source: this.uavGeoFenceSource,
      style: this.geoFenceLayerStyle
    });
    this.geoFenceLayer.set('name', UAV_GEOFENCE_LAYER_NAME);
    this.geoFenceLayer.setVisible(false);
    return this.geoFenceLayer;
  }

  getWaypointLayer() {
    this.waypointLayer = new VectorLayer({
      source: this.uavWaypointSource,
      style: this.waypointLayerStyle
    });
    this.waypointLayer.set('name', UAV_WAYPOINT_LAYER_NAME);
    this.waypointLayer.setVisible(false);

    return this.waypointLayer;
  }


  getDroneLayer() {

    const uavVectorSource = new VectorSource();
    this.droneLayer = new VectorLayer({
      source: uavVectorSource,
      zIndex: 100,
      style: this.uavStyle
    });
    const geoFenceCache: { [index: string]: GeoFence[] } = {};

    this.uavs.pipe(
      bufferTime(250),
      mergeMap(uavs => uavs),
      untilDestroyed(this)
    ).subscribe(uavs => {
      uavVectorSource.clear(true);
      this.uavWaypointSource.clear(true);

      const uavFeatures: Feature[] = [];
      const uavWaypoints: Feature[] = [];
      for (const uav of uavs) {
        try {

          const feature = this.geoJSON.readFeature(uav.location);
          feature.setId(uav.id);
          uavFeatures.push(feature);

          const homePoint = this.geoJSON.readFeature(uav.homePosition);
          homePoint.setStyle(this.homePointStyle);
          homePoint.set(UAVID, uav.id);
          uavFeatures.push(homePoint);

          const waypoints = this.getAllWaypointsAndMarkNext(uav.calculatedFlightPath, uav.nextWaypoint, uav.id);
          uavWaypoints.push(...waypoints);

          const coordinates = waypoints.map(w => (w.getGeometry() as Point).getCoordinates());
          const line = new Feature(new LineString(coordinates));
          line.set(UAVID, uav.id);
          uavWaypoints.push(line);

          const geoFence = uav.geoFence;
          if (geoFence) {

            const geoFenceFeatures = geoFence.map(fence => {
              const singleFeature = this.geoJSON.readFeature(fence.geometry);
              singleFeature.set(UAV_GEOFENCE_INCLUSION, fence.inclusion);
              singleFeature.set(UAVID, uav.id);
              return singleFeature;
            }, this);

            if (!geoFenceCache[uav.id]) {
              geoFenceCache[uav.id] = uav.geoFence;
              this.uavGeoFenceSource.addFeatures(geoFenceFeatures);
            }
            else {
              const cachedGeoFence = geoFenceCache[uav.id];
              if (cachedGeoFence.length !== uav.geoFence?.length) {
                this.uavGeoFenceSource.clear();
                this.uavGeoFenceSource.addFeatures(geoFenceFeatures);
                geoFenceCache[uav.id] = geoFence;
              }
            }

          }
        } catch (error) {
          console.log('error adding drones', error);
        }
      }
      uavVectorSource.addFeatures(uavFeatures);
      this.uavWaypointSource.addFeatures(uavWaypoints);
    });

    return this.droneLayer;
  }


  private getAllWaypointsAndMarkNext(waypoints: Waypoint[], nextWayoint: number, uavid: string): Feature<Geometry>[] {
    if (!waypoints) {
      return [];
    }

    return waypoints.map(w => {
      const feature = this.geoJSON.readFeature(w.geometry);

      const isNextWaypoint = w.waypointNo === nextWayoint;
      feature.set(IS_NEXT_WAYPOINT, isNextWaypoint);
      feature.set(UAVID, uavid);
      return feature;
    }, this);
  }

  getWaypointStyle(feature: Feature, resolution: number) {
    if (feature.get('nextWaypoint')) {
      return this.nextWaypointStyle;
    }
    return this.waypointStyle;
  }

  getUAVCommandLayer() {
    const myThis = this;

    const vectorLayer = new VectorLayer({
      source: this.uavCommandSource,
      style: this.uavCommandSelectStyle.bind(myThis),
      zIndex: 100
    });

    this.uavCommands.pipe(
      untilDestroyed(this)
    ).subscribe(uavCmds => {

      this.uavCommandSource.clear();
      const featuresToAdd: Feature[] = [];

      for (const cmd of uavCmds) {
        try {
          const cameraFeature = this.geoJSON.readFeature(cmd.cameraCommand.cameraPosition);
          cameraFeature.set(UAVID, getUAVIdFromUAVCommandId(cmd.id));
          cameraFeature.set(UAVCMD_ID, cmd.id);
          cameraFeature.set(UAVCMD_FEATURE_TYPE, FEATURE_TYPES.CAMERA_POINT);

          const uavPosFeature = this.geoJSON.readFeature(cmd.command.uavPosition);
          uavPosFeature.set(UAVID, getUAVIdFromUAVCommandId(cmd.id));
          uavPosFeature.set(UAVCMD_ID, cmd.id);
          uavPosFeature.set(UAVCMD_FEATURE_TYPE, FEATURE_TYPES.POI);

          featuresToAdd.push(cameraFeature);
          featuresToAdd.push(uavPosFeature);
        } catch (error) {
          console.log('error adding uavcommands', error);
        }
      }
      this.uavCommandSource.addFeatures(featuresToAdd);
    },
      error => console.log('error in uavcmd obs', error));

    return vectorLayer;
  }

  markUav(id: string) {
    this.markedUavId = id;
    this.waypointLayer.getSource().changed();
    this.geoFenceLayer.getSource().changed();
  }
}
